import { memo, useRef } from "react";
import * as PR from "../../prime-react/index";
import { editIcon } from "../../assets/images/index";

const MiddlePanel = memo(({
    isAnalyst,
    activeGroup,
    groupsData,
    sessionData,
    conversationSearchInput,
    onConversationSearchChange,
    conversationFilter,
    setConversationFilter,
    startNewConversation,
    paginatedConversations,
    conversationIds,
    editData,
    editHandler,
    renderEditTemplate,
    messageHandler,
    filteredConversations,
    getConversationTime,
    totalConversations,
    startIndex,
    endIndex,
    itemsPerPage,
    currentPage,
    totalPages,
    setCurrentPage,
    groupInitials,
    conversationSortOrder,
    setConversationSortOrder,
}) => {
    const activeGroupIndex = activeGroup ? groupsData.findIndex((g) => g.id === activeGroup.id) : 0;
    const sortMenu = useRef(null);

    const sortItems = [
        {
            label: 'Sort Conversations',
            items: [
                {
                    label: 'Default (Last Updated At)',
                    icon: conversationSortOrder === 'default' ? 'pi pi-check' : 'pi pi-fw',
                    command: () => setConversationSortOrder('default')
                },
                {
                    label: 'Created At',
                    icon: conversationSortOrder === 'createdAt' ? 'pi pi-check' : 'pi pi-fw',
                    command: () => setConversationSortOrder('createdAt')
                }
            ]
        }
    ];

    const getVisiblePages = () => {
        const pages = [];

        if (totalPages <= 7) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        pages.push(1);

        if (currentPage > 3) {
            pages.push("...");
        }

        const start = Math.max(2, currentPage - 1);
        const end = Math.min(totalPages - 1, currentPage + 1);

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }

        if (currentPage < totalPages - 2) {
            pages.push("...");
        }

        pages.push(totalPages);

        return pages;
    };

    const visiblePages = getVisiblePages();
    const groupName = isAnalyst ? (activeGroup?.name || sessionData.companyName) : "IBIA";

    return (
        <aside className="chat-sidebar conversations-panel">
            <div className="panel-heading panel-heading--compact">
                <div className="conversation-group-title">
                    <span className={`sidebar-avatar small avatar-color-${activeGroupIndex % 10}`}>{groupInitials(groupName)}</span>
                    <h2>{groupName}</h2>
                </div>
            </div>
            <div className="search-sort-container">
                <div className="chat-search">
                    <PR.InputText
                        type="text"
                        placeholder="Search conversations"
                        value={conversationSearchInput}
                        onChange={onConversationSearchChange}
                    />
                    <i className="pi pi-search" />
                </div>
                <button
                    type="button"
                    className={`chat-sort-btn ${conversationSortOrder !== "default" ? "active" : ""}`}
                    onClick={(e) => sortMenu.current.toggle(e)}
                    aria-label="Sort conversations"
                    aria-controls="conversations_sort_menu"
                    aria-haspopup
                >
                    <i className="pi pi-sliders-h" />
                </button>
                <PR.Menu model={sortItems} popup ref={sortMenu} id="conversations_sort_menu" className="sort_menu" />
            </div>
            <div className="conversation-actions">
                <div className="conversation-filters">
                    <button
                        type="button"
                        className={conversationFilter === "all" ? "active" : ""}
                        onClick={() => setConversationFilter("all")}
                    >
                        All
                    </button>
                    <button
                        type="button"
                        className={conversationFilter === "unread" ? "active" : ""}
                        onClick={() => setConversationFilter("unread")}
                    >
                        Unread
                    </button>
                </div>
                <PR.Button
                    type="button"
                    label="New conversation"
                    icon="pi pi-plus"
                    className="new-conversation-btn"
                    onClick={() => startNewConversation(activeGroup || {
                        id: sessionData.groupID,
                        name: sessionData.companyName,
                        hasExistingThread: true,
                    })}
                />
            </div>
            <div className="conversation-list">
                {paginatedConversations.map((conversation) => (
                    <button
                        type="button"
                        key={conversation.value}
                        className={`conversation-item ${conversation.value === conversationIds.conversationID && conversation.groupID === conversationIds.groupID ? "active" : ""}`}
                        onClick={() => messageHandler(conversation)}
                    >
                        <div className="conversation-row">
                            <span className="conversation-title-container">
                                 {editData?.value === conversation.value && editData?.groupID === conversation.groupID ? (
                            <div className="conversation-edit-row" onClick={(e) => e.stopPropagation()}>
                                {renderEditTemplate()}
                            </div>
                        ) :   <span className="conversation-title" title={conversation.label}>{conversation.label || "Untitled"}</span>}
                              
                            </span>
                            <span className="conversation-time">
                                {getConversationTime(conversation)}
                                <div className="flex align-items-center justify-content-end gap-2 mt-2">
                                 {conversation.badge ? <span className="conversation-badge">{conversation.badge}</span> : null}

                                {!conversation.isTemporary &&
                                    <PR.Image
                                        src={editIcon}
                                        alt="Rename conversation"
                                        className="edit-icon"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            editHandler(conversation);
                                        }}
                                    />
                                }
                                </div>
                            </span>
                        </div>
                    </button>
                ))}
                {!filteredConversations.length && (
                    <div className="conversation-empty-state">
                        <p>No conversations found.</p>
                    </div>
                )}
            </div>
            <div className="conversation-footer">
                <div className="conversation-footer-left">
                    {totalConversations > 0
                        ? `Showing ${startIndex} to ${endIndex} of ${totalConversations} conversations`
                        : "No conversations available"}
                </div>
                {totalConversations > itemsPerPage && (
                    <div
                        className="conversation-pagination"
                        role="navigation"
                        aria-label="Conversations pages"
                    >
                        <button
                            type="button"
                            className="page-nav"
                            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            aria-label="Previous page"
                        >
                            <i className="pi pi-chevron-left" />
                        </button>

                        {visiblePages.map((page, index) =>
                            page === "..." ? (
                                <span key={`dots-${index}`} className="page-dots">
                                    ...
                                </span>
                            ) : (
                                <button
                                    key={page}
                                    type="button"
                                    className={`page-btn ${page === currentPage ? "active" : ""}`}
                                    onClick={() => setCurrentPage(page)}
                                >
                                    {page}
                                </button>
                            )
                        )}

                        <button
                            type="button"
                            className="page-nav"
                            onClick={() =>
                                setCurrentPage((p) => Math.min(totalPages, p + 1))
                            }
                            disabled={currentPage === totalPages}
                            aria-label="Next page"
                        >
                            <i className="pi pi-chevron-right" />
                        </button>
                    </div>
                )}
            </div>
        </aside>
    );
});

export default MiddlePanel;
